import React, { useEffect, useMemo, useState } from 'react';
import { mediaUrl } from '../../utils/mediaUrl';
import { AdminSidebar } from './AdminDashboardPage';
import AdminHeader from './AdminHeader';
import {
  Package,
  AlertCircle,
  AlertTriangle,
  Info,
  Copy,
  Check,
  ExternalLink,
  Search,
  ChevronLeft,
  ChevronRight,
  X,
  Loader2
} from 'lucide-react';
import api from '../../services/api';
import '../../css/AdminCatalogHealthPage.css';

const SEVERITY_LABEL = { high: 'Fix First', medium: 'Should Fix', low: 'Nice to Fix' };
const SEVERITY_RANK = { high: 3, medium: 2, low: 1 };

const sortIssues = (issues) => {
  return [...issues].sort((a, b) => (SEVERITY_RANK[b.severity] || 0) - (SEVERITY_RANK[a.severity] || 0));
};

const PAGE_SIZE = 15;

// Lists product data problems (names, descriptions, photos, tags, SKUs)
// with suggested clean names and severity prioritization.
const AdminCatalogHealthPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [copied, setCopied] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    setLoading(true);
    api
      .get('/admin/catalog-health')
      .then((r) => {
        setData(r.data);
        setError('');
      })
      .catch((e) => setError(e.response?.data?.message || 'Could not load the catalog health report'))
      .finally(() => setLoading(false));
  }, []);

  // Reset pagination on filter or search change
  useEffect(() => {
    setCurrentPage(1);
  }, [filter, searchQuery]);

  // Dynamic counts for filter pills
  const filterCounts = useMemo(() => {
    if (!data?.rows) return { all: 0, high: 0, name: 0, medium: 0, low: 0 };
    return {
      all: data.rows.length,
      high: data.rows.filter((r) => r.issues.some((i) => i.severity === 'high')).length,
      name: data.rows.filter((r) => r.issues.some((i) => i.field === 'name')).length,
      medium: data.rows.filter((r) => r.issues.some((i) => i.severity === 'medium')).length,
      low: data.rows.filter((r) => r.issues.some((i) => i.severity === 'low')).length
    };
  }, [data]);

  // Filtered rows matching active filter pill and search query
  const filteredRows = useMemo(() => {
    if (!data?.rows) return [];
    let list = data.rows;
    if (filter !== 'all') {
      list = list.filter((r) =>
        r.issues.some((i) => (filter === 'name' ? i.field === 'name' : i.severity === filter))
      );
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      list = list.filter(
        (r) =>
          (r.name && r.name.toLowerCase().includes(q)) ||
          (r.subCategory && r.subCategory.toLowerCase().includes(q)) ||
          (r.suggestedName && r.suggestedName.toLowerCase().includes(q)) ||
          r.issues.some((i) => i.text && i.text.toLowerCase().includes(q))
      );
    }
    return list;
  }, [data, filter, searchQuery]);

  // Paginated slice
  const totalPages = Math.ceil(filteredRows.length / PAGE_SIZE) || 1;
  const paginatedRows = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return filteredRows.slice(start, start + PAGE_SIZE);
  }, [filteredRows, currentPage]);

  const copy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(text);
      setTimeout(() => setCopied(''), 2000);
    } catch (e) {
      /* clipboard blocked */
    }
  };

  const highCount = data?.counts?.high || 0;
  const mediumCount = data?.counts?.medium || 0;

  return (
    <div className="admin-layout">
      <AdminSidebar />
      <div className="admin-content">
        <AdminHeader title="Catalog Health" />

        <div className="ach-page">
          <div className="ach-head">
            <h1>Catalog Health</h1>
            <p>
              Audit and polish product data across your catalogue: unclear names, emoji, missing descriptions or photos,
              placeholder dashes, misaligned weave tags, and legacy SKUs. Suggested names follow the clean format{' '}
              <strong>Colour + Fabric + Type</strong>.
            </p>
          </div>

          {error && (
            <div className="ach-error-banner">
              <AlertCircle size={16} />
              <span>{error}</span>
            </div>
          )}

          {loading && (
            <div className="ach-loading">
              <Loader2 size={28} className="ach-spin" />
              <p>Analyzing catalog health and checking every product…</p>
            </div>
          )}

          {!loading && data && (
            <>
              {/* ---------- Metric Stat Cards ---------- */}
              <div className="ach-stats-grid">
                <div className="ach-stat-card">
                  <div className="ach-stat-top">
                    <span className="ach-stat-label">Total products</span>
                    <span className="ach-stat-icon">
                      <Package size={16} />
                    </span>
                  </div>
                  <div className="ach-stat-value">{data.totalProducts}</div>
                </div>

                <div className="ach-stat-card">
                  <div className="ach-stat-top">
                    <span className="ach-stat-label">Flagged products</span>
                    <span className="ach-stat-icon">
                      <AlertCircle size={16} />
                    </span>
                  </div>
                  <div className="ach-stat-value">{data.productsWithIssues}</div>
                </div>

                <div className={`ach-stat-card high-card ${highCount > 0 ? 'ach-stat-card--danger' : ''}`}>
                  <div className="ach-stat-top">
                    <span className="ach-stat-label">Fix first (high)</span>
                    <span className="ach-stat-icon">
                      <AlertTriangle size={16} />
                    </span>
                  </div>
                  <div className="ach-stat-value">{highCount}</div>
                </div>

                <div className={`ach-stat-card medium-card ${mediumCount > 0 ? 'ach-stat-card--warn' : ''}`}>
                  <div className="ach-stat-top">
                    <span className="ach-stat-label">Should fix (medium)</span>
                    <span className="ach-stat-icon">
                      <AlertCircle size={16} />
                    </span>
                  </div>
                  <div className="ach-stat-value">{mediumCount}</div>
                </div>

                <div className="ach-stat-card">
                  <div className="ach-stat-top">
                    <span className="ach-stat-label">Nice to fix (low)</span>
                    <span className="ach-stat-icon">
                      <Info size={16} />
                    </span>
                  </div>
                  <div className="ach-stat-value">{data.counts?.low || 0}</div>
                </div>
              </div>

              {/* ---------- Control Bar (Search & Filter Pills) ---------- */}
              <div className="ach-control-bar">
                <div className="ach-search-box">
                  <Search size={15} className="ach-search-icon" />
                  <input
                    type="text"
                    value={searchQuery}
                    placeholder="Search by product name, category, or issue..."
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                  {searchQuery && (
                    <button
                      type="button"
                      className="ach-search-clear"
                      aria-label="Clear search"
                      onClick={() => setSearchQuery('')}
                    >
                      <X size={15} />
                    </button>
                  )}
                </div>

                <div className="ach-filter-pills" role="group" aria-label="Filter problems">
                  {[
                    ['all', 'All Problems', filterCounts.all],
                    ['high', 'Fix First', filterCounts.high],
                    ['name', 'Name Problems', filterCounts.name],
                    ['medium', 'Should Fix', filterCounts.medium],
                    ['low', 'Nice to Fix', filterCounts.low]
                  ].map(([k, label, count]) => (
                    <button
                      key={k}
                      type="button"
                      className={`ach-filter-pill ${filter === k ? 'active' : ''}`}
                      onClick={() => setFilter(k)}
                    >
                      <span>{label}</span>
                      <span className="ach-pill-count">{count}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* ---------- Products List ---------- */}
              {filteredRows.length === 0 ? (
                <div className="ach-empty-card">
                  <div className="ach-empty-icon">
                    <Check size={22} />
                  </div>
                  <h3>All Clear in This Filter</h3>
                  <p>No products currently match your active filter criteria. Great job keeping the catalog clean!</p>
                </div>
              ) : (
                <div className="ach-list">
                  {paginatedRows.map((r) => {
                    const sortedIssues = sortIssues(r.issues || []);
                    return (
                      <div className="ach-product-card" key={r._id}>
                        <div className="ach-img-box">
                          {r.image ? (
                            <img
                              src={mediaUrl(r.image)}
                              alt={r.name || 'Product'}
                              loading="lazy"
                              onError={(e) => {
                                e.target.style.display = 'none';
                                if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex';
                              }}
                            />
                          ) : null}
                          <span
                            className="ach-img-fallback"
                            style={{ display: r.image ? 'none' : 'flex' }}
                          >
                            <Package size={22} />
                          </span>
                        </div>

                        <div className="ach-card-body">
                          <div className="ach-product-title-row">
                            <span className="ach-product-name">{r.name || '(Untitled Product)'}</span>
                            {r.subCategory && <span className="ach-cat-badge">{r.subCategory}</span>}
                            {r.status && r.status !== 'ACTIVE' && (
                              <span className="ach-status-badge">{r.status.toLowerCase()}</span>
                            )}
                          </div>

                          {r.suggestedName && r.suggestedName !== r.name && (
                            <div className="ach-suggest-box">
                              <span>Suggested name</span>
                              <strong>{r.suggestedName}</strong>
                              <button
                                type="button"
                                className={`ach-copy-btn ${copied === r.suggestedName ? 'copied' : ''}`}
                                onClick={() => copy(r.suggestedName)}
                                title="Copy suggested name to clipboard"
                              >
                                {copied === r.suggestedName ? (
                                  <>
                                    <Check size={12} /> Copied!
                                  </>
                                ) : (
                                  <>
                                    <Copy size={12} /> Copy
                                  </>
                                )}
                              </button>
                            </div>
                          )}

                          <ul className="ach-issues-list">
                            {sortedIssues.map((i, idx) => (
                              <li className="ach-issue-item" key={idx}>
                                <span className={`ach-severity-badge ${i.severity}`}>
                                  {i.severity === 'high' && <AlertTriangle size={11} />}
                                  {i.severity === 'medium' && <AlertCircle size={11} />}
                                  {i.severity === 'low' && <Info size={11} />}
                                  {SEVERITY_LABEL[i.severity] || i.severity}
                                </span>
                                <span>{i.text}</span>
                              </li>
                            ))}
                          </ul>
                        </div>

                        <div className="ach-card-actions">
                          <a
                            className="ach-btn-edit"
                            href={`/admin/products/textile/${r._id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            title="Open product editor in new tab"
                          >
                            <span>Edit Product</span>
                            <ExternalLink size={13} />
                          </a>
                          {r.slug && (
                            <a
                              className="ach-btn-shop"
                              href={`/product/${r.slug}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              title="View product in storefront"
                            >
                              <span>View in Shop</span>
                              <ExternalLink size={12} />
                            </a>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* ---------- Pagination Bar ---------- */}
              {filteredRows.length > PAGE_SIZE && (
                <div className="ach-pagination">
                  <span>
                    Showing <strong>{(currentPage - 1) * PAGE_SIZE + 1}</strong> -{' '}
                    <strong>{Math.min(currentPage * PAGE_SIZE, filteredRows.length)}</strong> of{' '}
                    <strong>{filteredRows.length}</strong> products
                  </span>
                  <div className="ach-pagination-btns">
                    <button
                      type="button"
                      className="ach-pg-btn"
                      disabled={currentPage === 1}
                      onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    >
                      <ChevronLeft size={15} /> Prev
                    </button>
                    <span className="ach-pg-current">
                      {currentPage} / {totalPages}
                    </span>
                    <button
                      type="button"
                      className="ach-pg-btn"
                      disabled={currentPage === totalPages}
                      onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                    >
                      Next <ChevronRight size={15} />
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminCatalogHealthPage;
